import { useDirection } from "@radix-ui/react-direction";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import * as React from "react";

import { cn } from "@/lib/utils";

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    viewportClassName?: string;
    viewportRef?:
      | React.RefObject<HTMLDivElement>
      | ((node: HTMLDivElement) => void);
    // NOTE: Opt-in horizontal scrollbar (NSAI-5213). The Radix viewport already
    // scrolls on both axes, but only the vertical bar is rendered by default, so
    // content wider than the viewport is clipped by the root's `overflow: hidden`
    // with nothing on screen to reach it. Opt-in rather than always-on: most
    // ScrollAreas in the app hold content that never exceeds their width, and an
    // unconditional bar would show up wherever a child overflows by a pixel.
    hasHorizontalScrollbar?: boolean;
    // NOTE: NSAI-5213 follow-up — Radix wraps the viewport's children in a div
    // it styles `min-width: 100%; display: table` (verified in
    // @radix-ui/react-scroll-area 1.2.10). `display: table` shrink-wraps to its
    // CONTENT, so a wide child — the Token & Cost Usage table, 11 columns —
    // makes that wrapper wider than the viewport. Every descendant `w-full`
    // then resolves against the EXPANDED width, which is why the table's own
    // `overflow-x-auto` never had a narrower box to scroll inside and the whole
    // settings panel scrolled sideways instead.
    //
    // Setting it back to `block` constrains children to the viewport, so an
    // inner scroll container works. Opt-in, because `display: table` is also
    // what lets a panel that WANTS to be wider than the viewport scroll as a
    // whole — switching it on globally would squash those.
    //
    // `!important` is required: Radix writes the rule as an INLINE style, which
    // no class can outrank without it.
    isViewportContentConstrained?: boolean;
  }
>(
  (
    {
      className,
      viewportClassName,
      viewportRef,
      hasHorizontalScrollbar = false,
      isViewportContentConstrained = false,
      children,
      ...props
    },
    ref,
  ) => {
    const dir = useDirection();
    return (
      <ScrollAreaPrimitive.Root
        dir={dir}
        ref={ref}
        className={cn("relative overflow-hidden", className)}
        {...props}
      >
        <ScrollAreaPrimitive.Viewport
          className={cn(
            "h-full w-full rounded-[inherit]",
            isViewportContentConstrained && "[&>div]:!block [&>div]:!w-full",
            viewportClassName,
          )}
          ref={viewportRef}
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        <ScrollBar />
        {hasHorizontalScrollbar ? <ScrollBar orientation="horizontal" /> : null}
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    );
  },
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      // NOTE: z-20 keeps the scrollbar above sticky content (e.g. bulk-action bars use z-10),
      // which otherwise paints over it since the Radix viewport is not its own stacking context.
      "z-20 flex touch-none select-none transition-colors",
      orientation === "vertical" &&
        "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" &&
        "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-scroll-area-thumb-background" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
